import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import mosidaLogo from "../assets/img/MOSIDA-logo.png";
import "./PWAInstallPrompt.scss";

const INSTALLABLE_EVENT = "pwa-installable";
const INSTALLED_EVENT = "pwa-installed";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    window.dispatchEvent(new Event(INSTALLABLE_EVENT));
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    window.dispatchEvent(new Event(INSTALLED_EVENT));
  });
}

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.matchMedia("(display-mode: fullscreen)").matches ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

function waitForNativePrompt(timeoutMs = 8000) {
  if (deferredPrompt) return Promise.resolve(true);
  return new Promise<boolean>((resolve) => {
    const done = (ok: boolean) => {
      window.removeEventListener(INSTALLABLE_EVENT, onReady);
      window.clearTimeout(timer);
      resolve(ok);
    };
    const onReady = () => done(true);
    const timer = window.setTimeout(() => done(Boolean(deferredPrompt)), timeoutMs);
    window.addEventListener(INSTALLABLE_EVENT, onReady);
  });
}

const PWAInstallPrompt = () => {
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onInstalled = () => {
      setInstalled(true);
      setDismissed(false);
    };
    window.addEventListener(INSTALLED_EVENT, onInstalled);
    return () => window.removeEventListener(INSTALLED_EVENT, onInstalled);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt && navigator.serviceWorker) {
      try {
        await navigator.serviceWorker.ready;
      } catch {
        /* ignore */
      }
      await waitForNativePrompt();
    }
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    if (choice.outcome === "accepted") setInstalled(true);
  }, []);

  const handleOpen = useCallback(() => {
    window.open(`${window.location.origin}/`, "_blank");
  }, []);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    setInstalled(false);
  }, []);

  if (isStandalone() || dismissed) return null;

  return createPortal(
    <div className="pwa-install-banner" role="dialog" aria-label="Install app">
      <img src={mosidaLogo} alt="" className="pwa-install-icon" />
      <div className="pwa-install-content">
        <span className="pwa-install-title">
          {installed ? "App installed" : "Install Digital Graffiti"}
        </span>
        <span className="pwa-install-text">
          {installed
            ? "Digital Graffiti was added to your device."
            : "Add it to your home screen for a full-screen kiosk experience."}
        </span>
      </div>
      <div className="pwa-install-actions">
        <button
          type="button"
          className="pwa-install-btn pwa-install-btn-dismiss"
          onClick={handleDismiss}
        >
          {installed ? "Close" : "Not now"}
        </button>
        <button
          type="button"
          className="pwa-install-btn pwa-install-btn-install"
          onClick={installed ? handleOpen : handleInstall}
        >
          {installed ? "Open app" : "Install"}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default PWAInstallPrompt;
